/**
 * ChatPulseThread — embeddable discussion widget.
 *
 * Anonymous readers see the entire thread + posts. Signed-in users
 * get an inline reply composer + reaction buttons. When there's no
 * thread for the context yet, we show a friendly "Start the discussion"
 * card that spawns a new thread on the fly.
 *
 * Usage:
 *   <ChatPulseThread
 *     contextType="event"
 *     contextId="np_evt_abc"
 *     contextLabel="Kochi Metro Phase 2 opens Monday"
 *   />
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MessageCircle, Loader2, Send, ThumbsUp, LogIn, Flag,
  Trash2, ExternalLink, Sparkles, Pin, Lock, Languages, RotateCcw,
  Share2, QrCode, Copy, X, ImagePlus, FileText, BarChart3, Video,
  AlertTriangle, Mic, MicOff, AtSign,
} from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/chatpulse/v1`;
const PREFERRED_LANG_KEY = "chatspulse.preferred_lang";

/** Load supported languages once per tab. */
let _LANG_CACHE = null;
async function loadLanguages() {
  if (_LANG_CACHE) return _LANG_CACHE;
  try {
    const r = await fetch(`${BASE}/languages`);
    if (!r.ok) return [];
    const d = await r.json();
    _LANG_CACHE = d.languages || [];
    return _LANG_CACHE;
  } catch {
    return [];
  }
}

function getPreferredLang() {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(PREFERRED_LANG_KEY);
  if (stored) return stored;
  const nav = (navigator.language || "en").split("-")[0].toLowerCase();
  return nav || "en";
}

function setPreferredLang(code) {
  try { window.localStorage.setItem(PREFERRED_LANG_KEY, code); } catch { /* noop */ }
}

/** Best-effort admin check — reads the cached user from localStorage. */
function isMasterAdmin() {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem("sp_user") || window.localStorage.getItem("user");
    if (!raw) return false;
    const u = JSON.parse(raw);
    if (u?.is_admin) return true;
    const role = (u?.platform_role || "").toLowerCase();
    return ["super_admin", "editor", "organization_admin"].includes(role);
  } catch { return false; }
}

/** Upload one image, return its media row. */
async function uploadMedia(file) {
  const token = getToken();
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${BASE}/media`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!r.ok) throw new Error(`upload_failed_${r.status}`);
  return r.json();
}

/** Small helper — absolute-ify a `/api/...` URL against REACT_APP_BACKEND_URL. */
function absUrl(pathOrUrl) {
  if (!pathOrUrl) return pathOrUrl;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${pathOrUrl}`;
}

/** Reactions palettes — mirrors backend chatpulse.live.REACTIONS_PALETTES. */
const DEFAULT_PALETTE = [
  { key: "like",       emoji: "👍", label: "Like" },
  { key: "love",       emoji: "❤️", label: "Love" },
  { key: "helpful",    emoji: "🙌", label: "Helpful" },
  { key: "insightful", emoji: "💡", label: "Insightful" },
];
const MEMORIAL_PALETTE = [
  { key: "condolence", emoji: "🙏", label: "Condolence" },
  { key: "love",       emoji: "❤️", label: "Love" },
  { key: "support",    emoji: "🤝", label: "Support" },
  { key: "candle",     emoji: "🕯️", label: "Light a candle" },
];
const EVENT_PALETTE = [
  { key: "celebrate",  emoji: "🎉", label: "Celebrate" },
  { key: "going",      emoji: "✋", label: "I'm going" },
  { key: "like",       emoji: "👍", label: "Like" },
  { key: "insightful", emoji: "💡", label: "Insightful" },
];
const SUPPORT_PALETTE = [
  { key: "support",    emoji: "🤝", label: "Support" },
  { key: "helpful",    emoji: "🙌", label: "Helpful" },
  { key: "hug",        emoji: "🫶", label: "Sending love" },
  { key: "prayer",     emoji: "🙏", label: "Prayer" },
];
const PALETTES = {
  default: DEFAULT_PALETTE, memorial: MEMORIAL_PALETTE,
  event: EVENT_PALETTE, support: SUPPORT_PALETTE,
};

function resolvePalette(name) {
  return PALETTES[name] || DEFAULT_PALETTE;
}

/** Start recording; returns a controller `{ stop, promise }`.
 *  `stop()` ends the recording and resolves the promise with the transcript.
 */
async function startRecording(preferredLang, onError) {
  if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
    onError?.("Microphone not supported in this browser.");
    return null;
  }
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    onError?.("Please allow microphone access.");
    return null;
  }
  const rec = new MediaRecorder(stream);
  const chunks = [];
  rec.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);

  const promise = new Promise((resolve) => {
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
      if (blob.size < 400) { onError?.("Recording too short."); resolve(null); return; }
      try {
        const token = getToken();
        const fd = new FormData();
        const ext = (rec.mimeType || "audio/webm").includes("mp4") ? "mp4" : "webm";
        fd.append("file", blob, `voice.${ext}`);
        const url = preferredLang ? `${BASE}/transcribe?language=${encodeURIComponent(preferredLang)}` : `${BASE}/transcribe`;
        const r = await fetch(url, {
          method: "POST", body: fd,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) throw new Error(`transcribe_${r.status}`);
        resolve(await r.json());
      } catch {
        onError?.("Transcription failed.");
        resolve(null);
      }
    };
  });

  rec.start();
  // Hard-cap 60 s so the browser doesn't hoard a mic forever.
  const cap = setTimeout(() => rec.state === "recording" && rec.stop(), 60000);
  return {
    promise,
    stop: () => { clearTimeout(cap); if (rec.state === "recording") rec.stop(); },
  };
}

function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  return `${mo}mo ago`;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function bodyToHtml(text) {
  return (text || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

async function api(path, opts = {}) {
  const token = getToken();
  const r = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    const err = new Error(`HTTP ${r.status}: ${detail}`);
    err.status = r.status;
    throw err;
  }
  return r.json();
}


export default function ChatPulseThread({
  contextType,
  contextId,
  contextLabel = "",
  maxThreads = 4,
  compact = false,
}) {
  const [threads, setThreads] = useState(null);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [preferredLang, setPreferredLangState] = useState(getPreferredLang());
  const signedIn = Boolean(getToken());
  useEffect(() => { loadLanguages().then(setLanguages); }, []);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        context_type: contextType,
        context_id: contextId,
        limit: String(maxThreads),
      });
      const d = await api(`/threads?${params.toString()}`);
      setThreads(d.threads || []);
    } catch (e) {
      setError(e.message || "load_failed");
    }
  }, [contextType, contextId, maxThreads]);

  useEffect(() => { load(); }, [load]);

  const onCreated = useCallback(async (thread) => {
    setThreads((prev) => [thread, ...(prev || [])]);
    setActiveId(thread.id);
    setStarting(false);
  }, []);

  if (error) return null; // silent — chat is optional on every page
  if (threads === null) {
    return (
      <div className="my-6 flex items-center gap-2 text-xs text-slate-400" data-testid="chatpulse-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading discussion…
      </div>
    );
  }

  return (
    <section
      className={`my-6 rounded-2xl border border-slate-200 bg-white overflow-hidden ${compact ? "" : "shadow-sm"}`}
      data-testid="chatpulse-thread"
      aria-labelledby="chatpulse-heading"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 inline-flex items-center justify-center">
            <MessageCircle className="w-4 h-4" />
          </span>
          <div>
            <h3 id="chatpulse-heading" className="text-sm font-bold text-slate-900">
              Discussion
            </h3>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              ChatsPulse · {threads.length} thread{threads.length === 1 ? "" : "s"}
            </p>
          </div>
        </div>
        {!starting && (
          <div className="flex items-center gap-2">
            <label className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              <Languages className="w-3 h-3" />
              <select
                value={preferredLang}
                onChange={(e) => {
                  setPreferredLang(e.target.value);
                  setPreferredLangState(e.target.value);
                }}
                className="text-xs rounded border border-slate-200 px-1 py-0.5 bg-white text-slate-700 normal-case tracking-normal font-medium"
                data-testid="chatpulse-preferred-lang"
                aria-label="Preferred reading language"
              >
                {languages.length ? languages.map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                )) : <option value={preferredLang}>{preferredLang}</option>}
              </select>
            </label>
            <button
              type="button"
              onClick={() => setStarting(true)}
              className="inline-flex items-center gap-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5"
              data-testid="chatpulse-start-thread"
            >
              <Sparkles className="w-3 h-3" /> Start a thread
            </button>
          </div>
        )}
      </header>

      {starting && (
        <NewThreadForm
          contextType={contextType}
          contextId={contextId}
          contextLabel={contextLabel}
          signedIn={signedIn}
          onCancel={() => setStarting(false)}
          onCreated={onCreated}
        />
      )}

      {threads.length === 0 && !starting ? (
        <div
          className="p-6 text-center text-sm text-slate-500"
          data-testid="chatpulse-empty"
        >
          No discussion yet. {signedIn ? "Start the first thread —" : "Sign in and start the first thread —"}{" "}
          <button
            type="button"
            onClick={() => setStarting(true)}
            className="text-blue-700 font-semibold hover:underline"
            data-testid="chatpulse-empty-start"
          >
            it takes 10 seconds.
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="chatpulse-thread-list">
          {threads.map((t) => (
            <li key={t.id}>
              <ThreadRow
                thread={t}
                open={activeId === t.id}
                onToggle={() => setActiveId((cur) => (cur === t.id ? null : t.id))}
                onDeleted={load}
                signedIn={signedIn}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


function NewThreadForm({ contextType, contextId, contextLabel, signedIn, onCancel, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [lang, setLang] = useState(getPreferredLang());
  const [languages, setLanguages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const titleRef = useRef(null);
  useEffect(() => { titleRef.current?.focus(); }, []);
  useEffect(() => { loadLanguages().then(setLanguages); }, []);

  if (!signedIn) {
    return (
      <div className="p-5 flex flex-col items-start gap-2 bg-blue-50/50 border-b border-slate-100">
        <LogIn className="w-5 h-5 text-blue-700" />
        <p className="text-sm text-slate-700">
          Sign in to start the first thread. Reading is open to everyone —
          writing is gated to keep the forum tidy.
        </p>
        <a
          href="/login"
          className="rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5"
          data-testid="chatpulse-signin-cta"
        >
          Sign in
        </a>
      </div>
    );
  }

  const canPost = title.trim().length >= 3 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canPost) return;
    setBusy(true);
    setError(null);
    try {
      const d = await api(`/threads`, {
        method: "POST",
        body: JSON.stringify({
          context_type: contextType,
          context_id: contextId,
          context_label: contextLabel || null,
          title: title.trim(),
          body_html: body.trim() ? bodyToHtml(body) : null,
          language: lang,
        }),
      });
      // Hydrate the response with what the list-view expects.
      onCreated({
        ...d,
        title: title.trim(),
        author: null,
        context_type: contextType,
        context_id: contextId,
        context_label: contextLabel || null,
        post_count: body.trim() ? 1 : 0,
        reaction_count: 0,
        last_post_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        pinned: false,
      });
    } catch (err) {
      setError(err.status === 429 ? "You're posting too fast — try again in a minute." : err.message);
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="p-4 space-y-2 bg-blue-50/40 border-b border-slate-100"
      data-testid="chatpulse-new-form"
    >
      <input
        ref={titleRef}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Ask a question or share a take…"
        maxLength={200}
        className="w-full text-sm font-semibold outline-none border border-slate-200 rounded-lg px-3 py-2 focus:border-blue-400"
        data-testid="chatpulse-new-title"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Add context (optional). Blank lines make new paragraphs."
        rows={3}
        maxLength={4000}
        className="w-full text-sm outline-none border border-slate-200 rounded-lg px-3 py-2 resize-none focus:border-blue-400"
        data-testid="chatpulse-new-body"
      />
      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {error}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 font-semibold">
          <Languages className="w-3 h-3" />
          <span className="uppercase tracking-widest">Language</span>
          <select
            value={lang}
            onChange={(e) => { setLang(e.target.value); setPreferredLang(e.target.value); }}
            className="text-xs rounded border border-slate-200 px-1.5 py-0.5 bg-white"
            data-testid="chatpulse-new-lang"
          >
            {languages.length ? languages.map((l) => (
              <option key={l.code} value={l.code}>{l.name} ({l.code})</option>
            )) : <option value={lang}>{lang}</option>}
          </select>
        </label>
        <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-slate-500 hover:text-slate-800"
          data-testid="chatpulse-new-cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canPost}
          className="inline-flex items-center gap-1 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-xs font-semibold px-3 py-1.5"
          data-testid="chatpulse-new-submit"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Start thread
        </button>
        </div>
      </div>
    </form>
  );
}


function ThreadRow({ thread, open, onToggle, onDeleted, signedIn }) {
  const [showShare, setShowShare] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promoted, setPromoted] = useState(thread.promoted_article_id || null);
  const admin = isMasterAdmin();

  const promote = async () => {
    if (!admin || promoting) return;
    if (!window.confirm(`Turn "${thread.title}" into a Publisher article draft?`)) return;
    setPromoting(true);
    try {
      const d = await api(`/threads/${thread.id}/promote-to-blog`, { method: "POST" });
      setPromoted(d.article_id);
      window.open(d.editor_url, "_blank", "noopener");
    } catch (e) {
      window.alert("Promote failed: " + e.message);
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className={`px-4 py-3 ${thread.pinned ? "bg-amber-50/50" : ""}`} data-testid={`chatpulse-row-${thread.id}`}>
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 text-left"
          data-testid={`chatpulse-row-toggle-${thread.id}`}
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold mb-0.5">
            {thread.pinned && (
              <span className="inline-flex items-center gap-0.5 text-amber-700"><Pin className="w-2.5 h-2.5" /> Pinned</span>
            )}
            {thread.status === "locked" && (
              <span className="inline-flex items-center gap-0.5 text-slate-500"><Lock className="w-2.5 h-2.5" /> Locked</span>
            )}
            <span className="text-slate-400">
              {thread.author?.name || "Someone"} · {timeAgo(thread.created_at)}
            </span>
          </div>
          <div className="text-sm font-semibold text-slate-900 leading-snug">
            {thread.title}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {thread.post_count} post{thread.post_count === 1 ? "" : "s"}
            {thread.reaction_count > 0 && ` · ${thread.reaction_count} 👍`}
            {thread.last_post_at && ` · latest ${timeAgo(thread.last_post_at)}`}
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setShowShare(true)}
            className="text-slate-400 hover:text-blue-700 p-1 rounded"
            title="Share thread (short URL + QR)"
            data-testid={`chatpulse-row-share-${thread.id}`}
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
          {admin && (
            <button
              type="button"
              onClick={promote}
              disabled={promoting || !!promoted}
              className="text-slate-400 hover:text-amber-700 p-1 rounded disabled:opacity-40"
              title={promoted ? "Already promoted" : "Promote to Publisher article"}
              data-testid={`chatpulse-row-promote-${thread.id}`}
            >
              {promoting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            </button>
          )}
          <a
            href={thread.canonical_url}
            target="_blank"
            rel="noopener"
            className="text-slate-400 hover:text-slate-700"
            title="Open on ChatsPulse"
            data-testid={`chatpulse-row-canonical-${thread.id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {open && (
        <ThreadPosts threadId={thread.id} thread={thread} signedIn={signedIn} onDeleted={onDeleted} />
      )}

      {showShare && (
        <ShareModal
          threadId={thread.id}
          title={thread.title}
          canonicalUrl={thread.canonical_url}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
}


function ShareModal({ threadId, title, canonicalUrl, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await api(`/threads/${threadId}/share`);
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e.message);
      }
    })();
    return () => { alive = false; };
  }, [threadId]);

  const copy = async () => {
    const url = data?.short_url || canonicalUrl;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`chatpulse-share-modal-${threadId}`}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Share</p>
            <h4 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2">{title}</h4>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-800"
            aria-label="Close share modal"
            data-testid={`chatpulse-share-close-${threadId}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {error && <div className="text-xs text-red-700">Failed to generate short URL. Copy the canonical link below.</div>}
        {!data && !error && (
          <div className="flex items-center gap-2 text-xs text-slate-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Minting short URL + QR…
          </div>
        )}
        {data && (
          <>
            {data.qr_png_url && (
              <div className="mb-4 flex justify-center">
                <img
                  src={absUrl(data.qr_png_url)}
                  alt="QR code for this thread"
                  className="w-48 h-48 border-2 border-slate-200 rounded-lg"
                  data-testid={`chatpulse-share-qr-${threadId}`}
                />
              </div>
            )}
            <div className="flex items-center gap-1 rounded-lg bg-slate-100 border border-slate-200 px-2 py-1.5">
              <QrCode className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <input
                readOnly
                value={data.short_url || canonicalUrl}
                className="flex-1 bg-transparent text-xs text-slate-800 outline-none min-w-0"
                onClick={(e) => e.target.select()}
                data-testid={`chatpulse-share-url-${threadId}`}
              />
              <button
                type="button"
                onClick={copy}
                className="inline-flex items-center gap-0.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold px-2 py-1"
                data-testid={`chatpulse-share-copy-${threadId}`}
              >
                <Copy className="w-3 h-3" />
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-2 text-center">
              Scan or share this link on posters, WhatsApp, or noticeboards.
            </p>
          </>
        )}
      </div>
    </div>
  );
}


function ThreadPosts({ threadId, thread, signedIn, onDeleted }) {
  const [posts, setPosts] = useState(null);
  const [reply, setReply] = useState("");
  const [replyLang, setReplyLang] = useState(getPreferredLang());
  const [replyMedia, setReplyMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [recorder, setRecorder] = useState(null); // { stop, promise } while recording
  const [transcribing, setTranscribing] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const palette = resolvePalette(thread.reactions_palette);
  useEffect(() => { loadLanguages().then(setLanguages); }, []);

  const toggleMic = async () => {
    if (recorder) {
      // Stop and wait for transcript.
      setTranscribing(true);
      recorder.stop();
      const result = await recorder.promise;
      setRecorder(null);
      setTranscribing(false);
      if (result?.text) {
        setReply((prev) => (prev ? prev + " " : "") + result.text);
        if (result.language) {
          setReplyLang(result.language);
          setPreferredLang(result.language);
        }
      }
      return;
    }
    const ctrl = await startRecording(replyLang, (msg) => setError(msg));
    if (ctrl) setRecorder(ctrl);
  };

  const onPickMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingMedia(true);
    try {
      const uploaded = [];
      for (const f of files) {
        try {
          const d = await uploadMedia(f);
          uploaded.push(d);
        } catch (err) {
          window.alert(`Upload failed for ${f.name}: ${err.message}`);
        }
      }
      setReplyMedia((prev) => [...prev, ...uploaded]);
    } finally {
      setUploadingMedia(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeMedia = (id) => {
    setReplyMedia((prev) => prev.filter((m) => m.id !== id));
  };

  const load = useCallback(async () => {
    try {
      const d = await api(`/threads/${threadId}/posts?limit=50`);
      setPosts(d.posts || []);
    } catch (e) {
      setError(e.message);
    }
  }, [threadId]);

  useEffect(() => { load(); }, [load]);

  const react = async (postId, hadReaction, kind = "like") => {
    // Optimistic — toggle: if user had this exact reaction, remove; else set new.
    const willHave = hadReaction === kind ? null : kind;
    setPosts((prev) => prev.map((p) => p.id === postId
      ? {
          ...p,
          reaction_count: p.reaction_count + (hadReaction ? (willHave ? 0 : -1) : (willHave ? 1 : 0)),
          my_reaction: willHave,
        }
      : p));
    try {
      await api(`/posts/${postId}/react`, {
        method: "POST",
        body: JSON.stringify({ kind: willHave || "clear" }),
      });
    } catch {
      // Revert on failure — cheap re-fetch.
      load();
    }
  };

  const softDelete = async (postId) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api(`/posts/${postId}`, { method: "DELETE" });
      load();
    } catch { /* silent */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (reply.trim().length < 1 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const d = await api(`/threads/${threadId}/posts`, {
        method: "POST",
        body: JSON.stringify({
          body_html: bodyToHtml(reply),
          language: replyLang,
          media_ids: replyMedia.map((m) => m.id),
        }),
      });
      setPosts((prev) => [...(prev || []), d]);
      setReply("");
      setReplyMedia([]);
    } catch (err) {
      setError(err.status === 429 ? "Too fast — try again in a minute." : err.message);
    } finally {
      setBusy(false);
    }
  };

  if (posts === null) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading replies…
      </div>
    );
  }

  return (
    <div className="mt-3 pl-3 border-l-2 border-slate-100 space-y-3" data-testid={`chatpulse-posts-${threadId}`}>
      <PollCard threadId={threadId} thread={thread} signedIn={signedIn} />
      {posts.length === 0 && (
        <div className="text-xs italic text-slate-400">No replies yet — be the first.</div>
      )}
      {posts.map((p) => (
        <PostRow
          key={p.id}
          post={p}
          signedIn={signedIn}
          languages={languages}
          palette={palette}
          onReact={(kind) => react(p.id, Boolean(p.my_reaction), kind)}
          onDelete={() => softDelete(p.id)}
        />
      ))}

      {thread.status === "locked" ? (
        <div className="text-xs italic text-slate-500 inline-flex items-center gap-1">
          <Lock className="w-3 h-3" /> This thread is locked — no new replies.
        </div>
      ) : signedIn ? (
        <form onSubmit={submit} className="space-y-2 pt-2" data-testid={`chatpulse-reply-form-${threadId}`}>
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Add a reply…"
            rows={2}
            maxLength={4000}
            className="w-full text-sm outline-none border border-slate-200 rounded-lg px-3 py-2 resize-none focus:border-blue-400"
            data-testid={`chatpulse-reply-body-${threadId}`}
          />
          {replyMedia.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid={`chatpulse-media-preview-${threadId}`}>
              {replyMedia.map((m) => (
                <div key={m.id} className="relative">
                  {m.kind === "video" || (m.content_type || "").startsWith("video/") ? (
                    <div className="w-16 h-16 rounded-lg border border-slate-200 bg-slate-900 flex items-center justify-center text-white">
                      <Video className="w-6 h-6" />
                    </div>
                  ) : (
                    <img
                      src={absUrl(m.url)}
                      alt=""
                      className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => removeMedia(m.id)}
                    className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-0.5 hover:bg-red-700"
                    title="Remove"
                    data-testid={`chatpulse-media-remove-${m.id}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {error && <div className="text-xs text-red-700">{error}</div>}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                <Languages className="w-3 h-3" />
                <select
                  value={replyLang}
                  onChange={(e) => { setReplyLang(e.target.value); setPreferredLang(e.target.value); }}
                  className="text-xs rounded border border-slate-200 px-1 py-0.5 bg-white"
                  data-testid={`chatpulse-reply-lang-${threadId}`}
                >
                  {languages.length ? languages.map((l) => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  )) : <option value={replyLang}>{replyLang}</option>}
                </select>
              </label>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                multiple
                className="hidden"
                onChange={onPickMedia}
                data-testid={`chatpulse-reply-file-${threadId}`}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploadingMedia}
                className="inline-flex items-center gap-1 rounded-full text-slate-600 hover:bg-slate-100 text-xs font-semibold px-2 py-1 disabled:opacity-50"
                title="Attach an image or short video"
                data-testid={`chatpulse-reply-media-${threadId}`}
              >
                {uploadingMedia ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
                Photo / Video
              </button>
              <button
                type="button"
                onClick={toggleMic}
                disabled={transcribing}
                className={`inline-flex items-center gap-1 rounded-full text-xs font-semibold px-2 py-1 disabled:opacity-50 ${
                  recorder
                    ? "bg-red-600 text-white hover:bg-red-700 animate-pulse"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
                title={recorder ? "Stop recording" : "Record voice reply"}
                data-testid={`chatpulse-reply-mic-${threadId}`}
              >
                {transcribing ? <Loader2 className="w-3 h-3 animate-spin" />
                  : recorder ? <MicOff className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
                {transcribing ? "Transcribing…" : recorder ? "Stop" : "Voice"}
              </button>
            </div>
            <button
              type="submit"
              disabled={busy || reply.trim().length < 1}
              className="inline-flex items-center gap-1 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-xs font-semibold px-3 py-1.5"
              data-testid={`chatpulse-reply-submit-${threadId}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Reply
            </button>
          </div>
        </form>
      ) : (
        <a
          href="/login"
          className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
          data-testid={`chatpulse-signin-inline-${threadId}`}
        >
          <LogIn className="w-3 h-3" /> Sign in to reply
        </a>
      )}
    </div>
  );
}


function PostRow({ post, signedIn, languages, palette = DEFAULT_PALETTE, onReact, onDelete }) {
  const isRemoved = post.status === "deleted_by_author" || post.status === "removed_by_mod";
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [translateError, setTranslateError] = useState(null);
  const originalLang = post.original_language || "en";
  const preferredLang = getPreferredLang();
  const canTranslate = !isRemoved && originalLang && originalLang !== preferredLang;
  const langMeta = useMemo(
    () => (languages || []).find((l) => l.code === originalLang),
    [languages, originalLang],
  );
  const preferredMeta = useMemo(
    () => (languages || []).find((l) => l.code === preferredLang),
    [languages, preferredLang],
  );

  const doTranslate = async () => {
    if (translating) return;
    setTranslating(true);
    setTranslateError(null);
    try {
      const d = await api(`/translate`, {
        method: "POST",
        body: JSON.stringify({
          target_type: "post",
          target_id: post.id,
          target_lang: preferredLang,
        }),
      });
      setTranslation(d);
      setShowOriginal(false);
    } catch (e) {
      setTranslateError("Translation failed — try again in a moment.");
    } finally {
      setTranslating(false);
    }
  };

  const bodyHtml = translation && !showOriginal ? translation.translated_html : post.body_html;

  return (
    <div
      className={`text-sm ${post.depth ? "ml-4" : ""}`}
      data-testid={`chatpulse-post-${post.id}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5 flex items-center gap-1.5 flex-wrap">
        <span>{post.author?.name || "Anon"} · {timeAgo(post.created_at)}</span>
        {originalLang && (
          <span
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-[1px] text-[9px] font-bold ${
              translation && !showOriginal
                ? "bg-emerald-100 text-emerald-800"
                : "bg-slate-100 text-slate-700"
            }`}
            title={langMeta?.name || originalLang}
            data-testid={`chatpulse-post-lang-${post.id}`}
          >
            <Languages className="w-2.5 h-2.5" />
            {(translation && !showOriginal ? translation.target_lang : originalLang).toUpperCase()}
            {translation && !showOriginal && (
              <span className="ml-0.5 text-emerald-700">· auto</span>
            )}
          </span>
        )}
      </div>
      <div
        className={`text-slate-800 leading-snug [&_p]:my-1 [&_a]:text-blue-700 [&_a]:underline ${isRemoved ? "italic text-slate-400" : ""}`}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: bodyHtml }}
      />
      {!isRemoved && (
        <div className="mt-1 flex items-center gap-2 text-xs flex-wrap">
          {signedIn && (
            <div className="inline-flex items-center gap-0.5" data-testid={`chatpulse-palette-${post.id}`}>
              {palette.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => onReact(r.key)}
                  className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-semibold transition-colors ${
                    post.my_reaction === r.key
                      ? "bg-blue-600 text-white"
                      : "text-slate-500 hover:bg-slate-100"
                  }`}
                  title={r.label}
                  aria-pressed={post.my_reaction === r.key}
                  data-testid={`chatpulse-react-${r.key}-${post.id}`}
                >
                  <span aria-hidden="true">{r.emoji}</span>
                  {post.my_reaction === r.key && post.reaction_count > 0 && (
                    <span className="text-[10px]">{post.reaction_count}</span>
                  )}
                </button>
              ))}
              {!post.my_reaction && post.reaction_count > 0 && (
                <span className="text-[10px] text-slate-500 ml-0.5">{post.reaction_count}</span>
              )}
            </div>
          )}
          {!signedIn && post.reaction_count > 0 && (
            <span className="text-slate-500">👍 {post.reaction_count}</span>
          )}
          {canTranslate && !translation && (
            <button
              type="button"
              onClick={doTranslate}
              disabled={translating}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-slate-600 hover:bg-slate-100 font-semibold"
              data-testid={`chatpulse-translate-${post.id}`}
              title={`Translate to ${preferredMeta?.name || preferredLang}`}
            >
              {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
              {translating ? "Translating…" : `Translate to ${preferredMeta?.name || preferredLang.toUpperCase()}`}
            </button>
          )}
          {translation && (
            <button
              type="button"
              onClick={() => setShowOriginal((v) => !v)}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-slate-600 hover:bg-slate-100 font-semibold"
              data-testid={`chatpulse-toggle-original-${post.id}`}
            >
              <RotateCcw className="w-3 h-3" />
              {showOriginal ? `See translation (${(preferredMeta?.name || preferredLang).toString()})` : `Show original (${(langMeta?.name || originalLang).toString()})`}
            </button>
          )}
          {translateError && (
            <span className="text-red-700 text-[11px]">{translateError}</span>
          )}
          {signedIn && (
            <button
              type="button"
              onClick={onDelete}
              className="text-slate-400 hover:text-red-600 inline-flex items-center gap-0.5 ml-auto"
              title="Delete this reply"
              data-testid={`chatpulse-delete-${post.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}



// ─── PollCard ───────────────────────────────────────────────────
const POLL_KIND_LABEL = {
  informal:     { label: "Informal community poll", tone: "bg-slate-100 text-slate-700" },
  org:          { label: "Organization-administered", tone: "bg-blue-100 text-blue-800" },
  consultation: { label: "Public consultation",       tone: "bg-emerald-100 text-emerald-800" },
};

function PollCard({ threadId, thread, signedIn }) {
  const [poll, setPoll] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [voting, setVoting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const canCreate = signedIn && (thread.author?.is_me || isMasterAdmin());

  const load = useCallback(async () => {
    try {
      const d = await api(`/threads/${threadId}/poll`);
      setPoll(d);
    } catch (e) {
      if (e.status !== 404) {
        // ignore — no poll on this thread
      }
    } finally {
      setLoaded(true);
    }
  }, [threadId]);
  useEffect(() => { load(); }, [load]);

  const vote = async (optId) => {
    if (voting) return;
    setVoting(true);
    try {
      const d = await api(`/polls/${poll.id}/vote`, {
        method: "POST",
        body: JSON.stringify({ option_ids: [optId] }),
      });
      setPoll(d);
    } catch (e) {
      window.alert("Vote failed: " + e.message);
    } finally {
      setVoting(false);
    }
  };

  if (!loaded) return null;
  if (!poll) {
    if (!canCreate || !showCreate) {
      return canCreate ? (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:text-blue-900 font-semibold"
          data-testid={`chatpulse-poll-add-${threadId}`}
        >
          <BarChart3 className="w-3 h-3" /> Add a poll to this thread
        </button>
      ) : null;
    }
    return (
      <NewPollForm
        threadId={threadId}
        onCancel={() => setShowCreate(false)}
        onCreated={(p) => { setPoll({ ...p, my_choices: [] }); setShowCreate(false); }}
      />
    );
  }

  const kindMeta = POLL_KIND_LABEL[poll.kind] || POLL_KIND_LABEL.informal;
  const total = poll.total_votes || 0;
  const closed = poll.closes_at && new Date(poll.closes_at) < new Date();

  return (
    <div
      className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2"
      data-testid={`chatpulse-poll-${poll.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${kindMeta.tone}`}>
            <BarChart3 className="w-2.5 h-2.5" /> {kindMeta.label}
          </p>
          <h4 className="text-sm font-semibold text-slate-900 mt-1">{poll.question}</h4>
        </div>
        <span className="text-[10px] text-slate-500 shrink-0">{total} vote{total === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-1.5">
        {poll.options.map((o) => {
          const pct = total > 0 ? Math.round((o.votes / total) * 100) : 0;
          const picked = (poll.my_choices || []).includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => signedIn && !closed && vote(o.id)}
              disabled={!signedIn || closed || voting}
              className={`relative w-full text-left rounded-lg border px-3 py-1.5 text-xs overflow-hidden transition-colors ${
                picked ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"
              } ${!signedIn || closed ? "cursor-default" : ""}`}
              data-testid={`chatpulse-poll-opt-${o.id}`}
            >
              <div className="absolute inset-y-0 left-0 bg-blue-100/70 pointer-events-none" style={{ width: `${pct}%` }} />
              <div className="relative flex items-center justify-between gap-2">
                <span className={`font-semibold ${picked ? "text-blue-900" : "text-slate-800"}`}>
                  {picked ? "✓ " : ""}{o.text}
                </span>
                <span className="text-slate-500">{pct}% · {o.votes}</span>
              </div>
            </button>
          );
        })}
      </div>
      {!signedIn && (
        <p className="text-[10px] text-slate-500">Sign in to vote.</p>
      )}
      {closed && <p className="text-[10px] text-slate-500 italic">This poll has closed.</p>}
      {poll.kind === "consultation" && (
        <p className="text-[10px] text-slate-500 italic">
          This is a community consultation. Results are not legally binding.
        </p>
      )}
    </div>
  );
}

function NewPollForm({ threadId, onCancel, onCreated }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState(["", ""]);
  const [kind, setKind] = useState("informal");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const d = await api(`/threads/${threadId}/poll`, {
        method: "POST",
        body: JSON.stringify({
          question: q.trim(),
          options: opts.map((o) => o.trim()).filter(Boolean),
          kind,
        }),
      });
      onCreated(d);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid={`chatpulse-poll-new-${threadId}`}>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Poll question"
        maxLength={200}
        className="w-full text-sm outline-none border border-slate-200 rounded-lg px-2 py-1"
        data-testid="chatpulse-poll-new-question"
      />
      {opts.map((o, i) => (
        <input
          key={i}
          value={o}
          onChange={(e) => setOpts((prev) => prev.map((x, idx) => idx === i ? e.target.value : x))}
          placeholder={`Option ${i + 1}`}
          maxLength={120}
          className="w-full text-xs outline-none border border-slate-200 rounded-lg px-2 py-1"
          data-testid={`chatpulse-poll-new-opt-${i}`}
        />
      ))}
      {opts.length < 8 && (
        <button
          type="button"
          onClick={() => setOpts((prev) => [...prev, ""])}
          className="text-[11px] text-blue-700 hover:text-blue-900 font-semibold"
        >
          + Add option
        </button>
      )}
      <div className="flex items-center gap-2">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="text-xs rounded border border-slate-200 px-1 py-0.5 bg-white"
          data-testid="chatpulse-poll-new-kind"
        >
          <option value="informal">Informal</option>
          <option value="org">Organization</option>
          <option value="consultation">Public consultation</option>
        </select>
        <span className="text-[10px] text-slate-500 flex-1">Not a legally binding election.</span>
      </div>
      {err && <p className="text-xs text-red-700">{err}</p>}
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="text-[11px] text-slate-500 hover:text-slate-800">Cancel</button>
        <button
          type="submit"
          disabled={busy || q.trim().length < 3 || opts.filter((o) => o.trim()).length < 2}
          className="inline-flex items-center gap-1 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-xs font-semibold px-3 py-1"
          data-testid="chatpulse-poll-new-submit"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <BarChart3 className="w-3 h-3" />} Create poll
        </button>
      </div>
    </form>
  );
}


// ─── EmergencyBoardBanner ────────────────────────────────────────
// Public component — pass in a place_id and it renders a banner
// listing active emergency boards (flood/cyclone/storm alerts) with
// a link into each pinned board.
const SEVERITY_TONE = {
  advisory: "bg-amber-50 border-amber-300 text-amber-900",
  watch:    "bg-orange-50 border-orange-300 text-orange-900",
  warning:  "bg-rose-50 border-rose-300 text-rose-900",
  critical: "bg-red-100 border-red-400 text-red-900",
};

export function EmergencyBoardBanner({ placeId, className = "" }) {
  const [boards, setBoards] = useState(null);
  useEffect(() => {
    if (!placeId) { setBoards([]); return; }
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/emergency-boards?place_id=${encodeURIComponent(placeId)}`);
        if (!r.ok) throw new Error("bad_status");
        const d = await r.json();
        if (alive) setBoards(d.boards || []);
      } catch {
        if (alive) setBoards([]);
      }
    })();
    return () => { alive = false; };
  }, [placeId]);

  if (!boards || boards.length === 0) return null;

  return (
    <div className={`space-y-2 ${className}`} data-testid="chatpulse-emergency-banner">
      {boards.map((b) => {
        const tone = SEVERITY_TONE[b.severity] || SEVERITY_TONE.advisory;
        return (
          <a
            key={b.id}
            href={`/chat?thread=${b.thread_id}`}
            className={`block rounded-xl border-2 px-4 py-3 hover:shadow-md transition-shadow ${tone}`}
            data-testid={`chatpulse-emergency-${b.id}`}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">
                  {b.severity} · {b.kind} · {b.place_name}
                </p>
                <p className="text-sm font-bold leading-snug mt-0.5">{b.headline}</p>
                <p className="text-[11px] mt-1 opacity-80">Join the community discussion →</p>
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );
}
